import { api } from '../../utils/client';
import type {
  AiModelOption,
  AiPromptTemplate,
  AiTestConceptTarget,
  AiTestJobOption,
  AiTestProject,
  AiTestRecipeTarget,
  AiTestRun,
  AiTestSourceOfTruthOption,
} from './types';

export function listTesterModels(generatorKey: string) {
  const params = new URLSearchParams({ generator_key: generatorKey });
  return api<AiModelOption[]>(`/api/ai-test/models/?${params.toString()}`);
}

export function refreshTesterModels() {
  return api<{
    results: Record<string, { created: number; existing: number; seen: number }>;
    errors: Record<string, string>;
  }>('/api/ai-test/models/refresh/', { method: 'POST' });
}

export function listTesterPrompts(generatorKey: string) {
  const params = new URLSearchParams({ generator_key: generatorKey });
  return api<AiPromptTemplate[]>(`/api/ai-test/prompts/?${params.toString()}`);
}

export function createTesterPrompt(payload: {
  generator_key: string;
  step_key?: string;
  name: string;
  content: string;
  notes?: string;
}) {
  return api<AiPromptTemplate>('/api/ai-test/prompts/', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function getTesterPromptContent(id: number) {
  return api<{ id: number; blob_filename: string; content: string }>(
    `/api/ai-test/prompts/${id}/content/`
  );
}

export function saveTesterPromptContent(id: number, content: string, name?: string) {
  return api<{ id: number; blob_filename: string; content: string }>(
    `/api/ai-test/prompts/${id}/content/`,
    { method: 'PUT', body: JSON.stringify({ content, name }) }
  );
}

export function getProductionPromptContent(blobFilename: string) {
  const params = new URLSearchParams({ blob_filename: blobFilename });
  return api<{ blob_filename: string; content: string }>(
    `/api/ai-test/prompts/production-content/?${params.toString()}`
  );
}

export function listTesterProjects() {
  return api<AiTestProject[]>('/api/ai-test/projects/');
}

export function listTesterSourceOfTruth(projectId: number) {
  const params = new URLSearchParams({ project_id: String(projectId) });
  return api<AiTestSourceOfTruthOption[]>(`/api/ai-test/source-of-truth/?${params.toString()}`);
}

export function listTesterJobs(projectId: number, market: string) {
  const params = new URLSearchParams({
    project_id: String(projectId),
    market,
  });
  return api<AiTestJobOption[]>(`/api/ai-test/jobs/?${params.toString()}`);
}

export function listTesterRecipeTargets(projectId: number) {
  const params = new URLSearchParams({ project_id: String(projectId) });
  return api<AiTestRecipeTarget[]>(`/api/ai-test/recipe-targets/?${params.toString()}`);
}

export function listTesterConceptTargets(projectId: number) {
  const params = new URLSearchParams({ project_id: String(projectId) });
  return api<AiTestConceptTarget[]>(`/api/ai-test/concept-targets/?${params.toString()}`);
}

export function listTesterRuns(projectId?: number, generatorKey?: string) {
  const params = new URLSearchParams();
  if (projectId) params.set('project_id', String(projectId));
  if (generatorKey) params.set('generator_key', generatorKey);
  const qs = params.toString();
  return api<AiTestRun[]>(`/api/ai-test/runs/${qs ? `?${qs}` : ''}`);
}

export function createTesterRun(payload: Record<string, unknown>) {
  return api<AiTestRun>('/api/ai-test/runs/', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function getTesterRun(id: number) {
  return api<AiTestRun>(`/api/ai-test/runs/${id}/`);
}

export function getTesterRunArtifact(runId: number, jobId?: string) {
  const params = new URLSearchParams();
  if (jobId) params.set('job_id', jobId);
  const qs = params.toString();
  return api<{
    run_id: number;
    job_id: string;
    content: string;
    usage: Record<string, unknown>;
    cost_usd: string | null;
    jtbd_statements?: string[];
    intermediate_results?: Record<string, unknown>;
    relationship_json?: Array<Record<string, unknown>>;
    tensions?: Array<{ tension_text?: string; text?: string; tension?: string; metadata?: Record<string, unknown> } | string>;
    job_to_be_done?: string;
    market?: string;
    attributes?: Array<{
      name?: string;
      error?: string;
      options?: {
        established?: Array<{ option_text?: string } | string>;
        emerging?: Array<{ option_text?: string } | string>;
        breakthrough?: Array<{ option_text?: string } | string>;
      } | null;
    }>;
    tab_name?: string;
    brand_name?: string;
    format_name?: string;
    combination_name?: string;
    written_concept?: string;
    image_url?: string;
    image_b64?: string;
    mime_type?: string;
    visual_mode?: string;
  }>(`/api/ai-test/runs/${runId}/artifact/${qs ? `?${qs}` : ''}`);
}
